/**
 * A avaliação da formalização e a revisão do perito (E152.F3, GUS-175, ADR-0103 §5.9): o contrato
 * de `GET /api/casos/{id}/formalizacao/avaliacao` e de `POST .../revisoes`, espelhado à mão dos
 * tipos do servidor e preso pelo teste de paridade (`tests/unit/test_e152_f3_contrato_web.py`).
 * A avaliação é do servidor: a tela não decide a conclusão, não reordena leituras e não deduz que
 * revisão vale — o servidor diz o que está pendente e o que está em vigor.
 */
import axios, { type AxiosInstance } from 'axios';

import type {
  CategoriaDoRequisito,
  FonteDaForma,
  ModoDoExame,
  PapelDoRequisito,
  PendenciaData,
} from '@/lib/formalizacao';

import type { ClausulaPeloMotorData, LocalizacaoDaClausulaData } from './clausulaDaAutorizacao';

export type ConclusaoDaFormalizacao = 'regular' | 'irregular' | 'nao_demonstrada' | 'pendente';
export type MotivoDaPendencia =
  | 'prontidao'
  | 'pendencia_normativa'
  | 'revisao_especializada'
  | 'diligencia_decisiva';
export type SituacaoDaLeitura =
  | 'sustenta'
  | 'sustenta_sob_revisao'
  | 'nao_sustenta'
  | 'irregular'
  | 'pendente_normativa'
  | 'inadmissivel';
export type SituacaoDoRequisito =
  | 'demonstrado'
  | 'aguarda_revisao'
  | 'nao_demonstrado'
  | 'contrariado'
  | 'pendente_normativo'
  | 'nao_decisivo';
export type SituacaoDaRota = 'completa' | 'aguarda_revisao' | 'incompleta' | 'inativa';
export type IdDaRota = 'SUF-01' | 'SUF-02' | 'SUF-03' | 'SUP-F-3';
export type LadoDoElemento = 'pessoa' | 'ato' | 'registro_primario' | 'forma' | 'contraprova';
export type SituacaoDoElemento =
  | 'demonstrado'
  | 'proposto'
  | 'inconclusivo'
  | 'ausente'
  | 'nao_extraido';
export type NaturezaDaFonte = 'declarado' | 'apurado' | 'consulta_externa';
export type TipoDoElemento =
  | 'face_positiva'
  | 'documento_na_sessao'
  | 'credencial_do_titular'
  | 'credito_em_conta'
  | 'evento_de_aceite'
  | 'registro_com_carimbo'
  | 'declaracao_do_meio'
  | 'captura_coincidente'
  | 'autenticacao_efetiva'
  | 'assinatura_digital_do_participante'
  | 'integridade_do_instrumento'
  | 'decisao_biometrica'
  | 'prova_de_vida'
  | 'autenticacao_multifator'
  | 'termo_de_desbloqueio'
  | 'instrumento_de_consignado'
  | 'autorizacao_do_desconto'
  | 'face_divergente'
  | 'dados_essenciais_divergentes'
  | 'conteudo_alterado';
export type RevisaoEspecializada = 'RP-04' | 'RP-08';
/** E184.F3 (GUS-145 item 4): `credito` e `integridade` são o aceite com fundamento da divergência. */
export type TipoDaRevisao =
  | 'face'
  | 'contraprova'
  | 'suprimento'
  | 'diligencias'
  | 'autorizacao'
  | 'credito'
  | 'integridade';
export type DecisaoDaRevisao =
  | 'confirma_positiva'
  | 'confirma_divergente'
  | 'mantem_inconclusiva'
  | 'confirmada'
  | 'afastada'
  | 'aceito'
  | 'recusado'
  | 'encerradas'
  | 'mantidas'
  | 'localizada'
  | 'nao_localizada'
  | 'aceita'
  | 'mantida';
export type NaturezaDoAchado =
  | 'contraprova'
  | 'limite_essencial'
  | 'limite_nao_decisivo'
  | 'exigencia_legal'
  | 'diligencia_decisiva'
  | 'pendencia_normativa'
  | 'tese_alternativa'
  | 'item_nao_comprovado'
  | 'reforco';
export type DestinoDoAchado = 'laudo_essencial' | 'riscos_reservados';
export type GrauDoRisco = 'alto' | 'medio' | 'baixo';
export type EstadoDaAnalise = 'concluida' | 'nao_executada';
export type EstadoDaVeracidade = 'aprovado' | 'corrigir' | 'bloqueado';
export type EstadoDoCriterio = 'passou' | 'falhou' | 'inconclusivo' | 'nao_se_aplica_justificado';
export type ChaveDoCriterio =
  | 'suporte'
  | 'teste_registrado'
  | 'limites_essenciais'
  | 'destino_do_essencial'
  | 'reservado_so_em_parte';

/** As decisões de cada tipo de revisão, na ordem do servidor (`DECISOES_DO_TIPO`). */
export const DECISOES_DO_TIPO: Readonly<Record<TipoDaRevisao, readonly DecisaoDaRevisao[]>> = {
  face: ['confirma_positiva', 'confirma_divergente', 'mantem_inconclusiva'],
  contraprova: ['confirmada', 'afastada'],
  suprimento: ['aceito', 'recusado'],
  diligencias: ['encerradas', 'mantidas'],
  autorizacao: ['localizada', 'nao_localizada'],
  credito: ['aceita', 'mantida'],
  integridade: ['aceita', 'mantida'],
};

export interface ElementoDeProvaData {
  id: string;
  tipo: TipoDoElemento;
  lado: LadoDoElemento;
  situacao: SituacaoDoElemento;
  natureza: NaturezaDaFonte;
  origem: string;
  fonte: string;
  alcance: string;
  revisao: RevisaoEspecializada | null;
  depende_de: string[];
  /** E172.F1 (GUS-230 B2.4): o instante real do fato e o do carimbo que o registra; nulos sem fonte. */
  instante: string | null;
  carimbo: string | null;
  localizacao?: LocalizacaoDaClausulaData; // E172.F8: a localizada pelo sistema na CCB
  /** E178.F3.C05: no crédito em conta com mais de um comprovante, o resultado de cada arquivo. */
  por_arquivo?: CreditoDoArquivoData[];
}

export type SituacaoDoArquivo =
  | 'conferido'
  | 'divergente'
  | 'conta_nao_conferida'
  | 'nao_legivel'
  | 'nao_conferido';

export interface CreditoDoArquivoData {
  documento_id: string;
  onde: string;
  situacao: SituacaoDoArquivo;
}

export interface AvaliacaoDaRotaData {
  rota: IdDaRota;
  situacao: SituacaoDaRota;
  elementos: string[];
  faltam: string[];
  motivo: string;
}

export interface AvaliacaoDoRequisitoData {
  requisito_id: string;
  titulo: string;
  papel: PapelDoRequisito;
  categoria: CategoriaDoRequisito;
  situacao: SituacaoDoRequisito;
  rota: IdDaRota | null;
  elementos: string[];
  faltam: string[];
  motivo: string;
}

export interface AvaliacaoDaLeituraData {
  perfil_id: string;
  leitura_id: string;
  data: string | null;
  titulo: string;
  fonte_da_forma: FonteDaForma;
  prioridade: number;
  subsidiaria: boolean;
  situacao: SituacaoDaLeitura;
  requisitos: AvaliacaoDoRequisitoData[];
  motivo: string;
}

export interface DiligenciaData {
  id: string;
  requisito_id: string;
  descricao: string;
  decisiva: boolean;
}

export interface RevisaoPendenteData {
  revisao: RevisaoEspecializada;
  tipo: TipoDaRevisao;
  objeto: string;
  descricao: string;
  /** E172.F6 (B1.2): só no ponto da autorização; ausente no ponto montado para rever. */
  clausula?: ClausulaPeloMotorData | null;
  valor_lido?: string; // E184.F3: na divergência que se aceita, o que o motor leu (não muda)
}

/** E172.F6/F8: a cláusula de autorização do desconto, em `clausulaDaAutorizacao`. */
export type {
  ClausulaPeloMotorData,
  ClausulaPropostaData,
  CriterioDaLocalizacao,
  LocalizacaoDaClausulaData,
  SituacaoDaLocalizacao,
} from './clausulaDaAutorizacao';
export { TETO_DO_DOCUMENTO, TETO_DO_TRECHO } from './clausulaDaAutorizacao';

/** E172.F4 (GUS-230 B2.3): a decisão do assistente técnico sobre um item do parecer facial. */
export type DecisaoDoItem = 'confirmado' | 'retificado';

export interface VerificacaoDoItemData {
  /** `qualidade_documento_identidade`, `qualidade_ccb` ou o elemento do painel. */
  item: string;
  decisao: DecisaoDoItem;
  /** Obrigatória no item retificado: o que o assistente técnico observa no lugar. */
  observacao: string | null;
}

export interface RevisaoEmVigorData {
  revisao: RevisaoEspecializada;
  tipo: TipoDaRevisao;
  objeto: string;
  decisao: DecisaoDaRevisao;
  fundamento: string;
  /** E168.F5 (AJ-02): quem decidiu e quando, do registro; nulos fora dele. */
  revisor: string | null;
  em: string | null;
  localizacao: LocalizacaoDaClausulaData | null;
  /** E172.F4 (GUS-230 B2.3): na face, a verificação item a item do assistente técnico. */
  verificacao: VerificacaoDoItemData[];
  valor_lido?: string; // E184.F3: no aceite com fundamento, o valor lido que ele não mudou
}

export interface AvaliacaoDaFormalizacaoData {
  modo: ModoDoExame;
  catalogo_versao: string;
  catalogo_sha256: string;
  conclusao: ConclusaoDaFormalizacao;
  proposta: ConclusaoDaFormalizacao | null;
  motivos_da_pendencia: MotivoDaPendencia[];
  leitura_escolhida: string | null;
  razao_da_escolha: string | null;
  leituras: AvaliacaoDaLeituraData[];
  rotas: AvaliacaoDaRotaData[];
  elementos: ElementoDeProvaData[];
  diligencias: DiligenciaData[];
  revisoes_pendentes: RevisaoPendenteData[];
  revisoes_em_vigor: RevisaoEmVigorData[];
  pendencias_normativas: PendenciaData[];
  /**
   * E172.F15 (ADR-0128 §4): em PARTE, o que o sistema apurou sem revisão exigida (face, contraprova
   * e cláusula localizada) e que o assistente técnico pode revisar se julgar necessário. Não é
   * pendência; ausente quando vazio.
   */
  revisoes_facultativas?: RevisaoPendenteData[];
}

export interface RiscoDoAchadoData {
  grau: GrauDoRisco;
  motivo: string;
  fundamento_atingido: string;
  argumento_contrario: string;
  resposta: string | null;
  providencia: string;
}

export interface AchadoData {
  id: string;
  natureza: NaturezaDoAchado;
  fato: string;
  fonte: string;
  requisitos: string[];
  destinos: DestinoDoAchado[];
  motivo_do_destino: string;
  risco: RiscoDoAchadoData | null;
  elementos: string[];
}

export interface AnaliseDosAchadosData {
  estado: EstadoDaAnalise;
  criterio: string;
  cobertura: string;
  achados: AchadoData[];
}

export interface AfirmacaoData {
  id: string;
  texto: string;
  requisito_id: string | null;
  elementos: string[];
  afirma_execucao: TipoDoElemento | null;
}

export interface CriterioDaVeracidadeData {
  chave: ChaveDoCriterio;
  estado: EstadoDoCriterio;
  bloqueante: boolean;
  detalhe: string;
}

export interface VeracidadeDaAvaliacaoData {
  estado: EstadoDaVeracidade;
  versao: string;
  criterios: CriterioDaVeracidadeData[];
}

export interface ResultadoDaFormalizacaoData {
  avaliacao: AvaliacaoDaFormalizacaoData;
  achados: AnaliseDosAchadosData;
  fundamentos: AfirmacaoData[];
  veracidade: VeracidadeDaAvaliacaoData;
}

export interface AvaliacaoGravadaData {
  id: string;
  case_id: string;
  versao: number;
  modo: ModoDoExame;
  catalogo_versao: string;
  catalogo_sha256: string;
  entrada_sha256: string;
  /** E153.F1 (ADR-0104 §2.1): a impressão do fluxo da versão; nula na anterior à migration 0138. */
  fluxo_sha256: string | null;
  sha256: string;
  conclusao: ConclusaoDaFormalizacao;
  proposta: ConclusaoDaFormalizacao | null;
  qa: EstadoDaVeracidade;
  conteudo: ResultadoDaFormalizacaoData;
  criada_por: string | null;
  criada_em: string;
}

export interface RevisaoRegistradaData {
  id: string;
  case_id: string;
  avaliacao_id: string;
  avaliacao_versao: number;
  revisao: RevisaoEspecializada;
  tipo: TipoDaRevisao;
  objeto: string;
  decisao: DecisaoDaRevisao;
  fundamento: string;
  criada_por: string;
  criada_em: string;
  /** E168.F5 (AJ-02): o nome de exibição (ou o e-mail) de quem decidiu. */
  revisor: string | null;
  localizacao: LocalizacaoDaClausulaData | null;
  verificacao: VerificacaoDoItemData[];
  valor_lido: string | null; // E184.F3: o valor lido no aceite, gravado da versão em tela
}

export interface PainelDaFormalizacaoData {
  case_id: string;
  avaliacao: AvaliacaoGravadaData;
  revisoes: RevisaoRegistradaData[];
}

export interface RevisaoInData {
  versao: number;
  objeto: string;
  decisao: DecisaoDaRevisao;
  fundamento: string;
  /** E168.F5 (AJ-03): obrigatória na cláusula localizada; o servidor recusa a falta. */
  localizacao?: LocalizacaoDaClausulaData | null;
  /** E172.F4 (GUS-230 B2.3): só na face — uma decisão por item do parecer (a qualidade de cada
   * imagem e cada componente); o servidor recusa item faltando, sobrando ou repetido. */
  verificacao?: VerificacaoDoItemData[];
}

export type MotivoDaFalhaDaAvaliacao =
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'desatualizada'
  | 'fora_da_pendencia'
  | 'invalida'
  | 'localizacao_incompleta'
  | 'verificacao_incompleta'
  | 'indisponivel';

export class AvaliacaoDaFormalizacaoError extends Error {
  readonly motivo: MotivoDaFalhaDaAvaliacao;

  constructor(motivo: MotivoDaFalhaDaAvaliacao) {
    super(`avaliação da formalização: ${motivo}`);
    this.name = 'AvaliacaoDaFormalizacaoError';
    this.motivo = motivo;
  }
}

const _POR_DETALHE: Readonly<Record<string, MotivoDaFalhaDaAvaliacao>> = {
  AVALIACAO_DESATUALIZADA: 'desatualizada',
  REVISAO_FORA_DA_PENDENCIA: 'fora_da_pendencia',
  LOCALIZACAO_INCOMPLETA: 'localizacao_incompleta',
  VERIFICACAO_INCOMPLETA: 'verificacao_incompleta',
};
const _POR_STATUS: Readonly<Record<number, MotivoDaFalhaDaAvaliacao>> = {
  403: 'sem_permissao',
  404: 'nao_encontrado',
  422: 'invalida',
};

function motivoDoErro(err: unknown): MotivoDaFalhaDaAvaliacao {
  if (!axios.isAxiosError(err) || err.response === undefined) return 'indisponivel';
  const detalhe: unknown = (err.response.data as { detail?: unknown } | undefined)?.detail;
  const pelaRecusa = typeof detalhe === 'string' ? _POR_DETALHE[detalhe] : undefined;
  return pelaRecusa ?? _POR_STATUS[err.response.status] ?? 'indisponivel';
}

export async function lerAvaliacaoDaFormalizacao(
  client: AxiosInstance,
  caseId: string,
): Promise<PainelDaFormalizacaoData> {
  try {
    const { data } = await client.get<PainelDaFormalizacaoData>(
      `/casos/${caseId}/formalizacao/avaliacao`,
    );
    return data;
  } catch (err) {
    throw new AvaliacaoDaFormalizacaoError(motivoDoErro(err));
  }
}

export async function enviarRevisaoDoPerito(
  client: AxiosInstance,
  caseId: string,
  revisao: RevisaoInData,
): Promise<PainelDaFormalizacaoData> {
  try {
    const { data } = await client.post<PainelDaFormalizacaoData>(
      `/casos/${caseId}/formalizacao/revisoes`,
      revisao,
    );
    return data;
  } catch (err) {
    throw new AvaliacaoDaFormalizacaoError(motivoDoErro(err));
  }
}

/** A leitura que sustenta a conclusão ou a proposta, como o servidor a escolheu. */
export function leituraEscolhida(
  a: AvaliacaoDaFormalizacaoData,
): AvaliacaoDaLeituraData | undefined {
  return a.leituras.find((l) => `${l.perfil_id}/${l.leitura_id}` === a.leitura_escolhida);
}
